import { collection, doc, getDoc, query, where, getDocs, onSnapshot, orderBy, limit } from 'firebase/firestore'
import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { db } from '../../firebase'
import {
  localizeCategory,
  loadCategoryCatalog,
  toCategoryKeys,
  LEGACY_CATEGORY_PREFIX,
  type CategoryCatalogItem,
} from '../../utils/categoryResolver'
import { Box, Typography, CircularProgress, Alert, FormControlLabel, Checkbox, Paper, Collapse, Button, TextField, Autocomplete, Chip, Tooltip } from '@mui/material'
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

/** المسافة بالكم بين نقطتين (هافرساين) */
function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function normalizePhoneDigits(v: string): string {
  return (v || '').toString().replace(/\D/g, '')
}

/** خيار فلتر: مفتاح مستقر للمطابقة + اسم للعرض + عدد التجار */
interface CategoryFilterOption {
  key: string
  label: string
  isLegacy: boolean
  count: number
}

/** مفتاح خيالي لتجار بلا فئات (يظهر كخيار مستقل في الفلتر) */
const NO_CATEGORY_KEY = '__no_category__'

interface MerchantLocation {
  id: string
  sellerId: string
  sellerName: string
  storeName: string
  latitude: number
  longitude: number
  status: string
  categories: string[]
  /** القيم الخام المخزّنة في Firestore (معرّفات أو أسماء) لتطابق دقيق */
  rawCategories: string[]
  /** أرقام الهاتف فقط للمطابقة عند البحث */
  phone: string
  isActive: boolean
  lastSeenAt: Date | null
}

// أيقونة التاجر: دائرة سوداء بحرف K برتقالي + شارة حالة (✕ / ✓ / ✓✓) حسب حالة الطلب
const K_ICON_SIZE = 16
const K_COLOR = '#ff6600'

type MerchantRequestStatus = 'none' | 'notAvailable' | 'offerSent' | 'offerAccepted'

function createMerchantKIcon(options: { highlighted: boolean; status: MerchantRequestStatus; isActive: boolean }) {
  const { highlighted, status, isActive } = options

  let badge = ''
  if (status === 'notAvailable') {
    badge =
      '<div style="position:absolute;bottom:-4px;right:-4px;background:#fff;border-radius:8px;font-size:9px;color:#d32f2f;font-weight:700;padding:0 2px;border:1px solid rgba(0,0,0,0.2)">✕</div>'
  } else if (status === 'offerSent') {
    badge =
      '<div style="position:absolute;bottom:-4px;right:-4px;background:#fff;border-radius:8px;font-size:9px;color:#2e7d32;font-weight:700;padding:0 2px;border:1px solid rgba(0,0,0,0.2)">✓</div>'
  } else if (status === 'offerAccepted') {
    badge =
      '<div style="position:absolute;bottom:-4px;right:-4px;background:#fff;border-radius:8px;font-size:9px;color:#2e7d32;font-weight:700;padding:0 2px;border:1px solid rgba(0,0,0,0.2)">✓✓</div>'
  }

  const borderWidth = highlighted ? 2 : 1
  const borderColor = highlighted ? K_COLOR : 'rgba(255,255,255,0.6)'
  const boxShadow = highlighted ? `0 0 6px ${K_COLOR}` : '0 0 2px rgba(0,0,0,0.4)'
  const bgColor = isActive ? K_COLOR : '#111111'
  const textColor = isActive ? '#ffffff' : '#ffffff'

  const html = `<div style="position:relative;width:${K_ICON_SIZE}px;height:${K_ICON_SIZE}px;border-radius:50%;background:${bgColor};color:${textColor};font-weight:bold;font-size:10px;display:flex;align-items:center;justify-content:center;border:${borderWidth}px solid ${borderColor};box-shadow:${boxShadow};box-sizing:border-box;line-height:1">K${badge}</div>`

  return L.divIcon({
    className: 'merchant-k-marker',
    html,
    iconSize: [K_ICON_SIZE, K_ICON_SIZE],
    iconAnchor: [K_ICON_SIZE / 2, K_ICON_SIZE / 2],
    popupAnchor: [0, -K_ICON_SIZE / 2],
  })
}

/** محاكاة طلب: مركز نطاق البحث، نصف القطر، وفئة الطلب (لإظهار التجار المختصين بها فقط) */
interface RequestSimulation {
  center: [number, number]
  radiusKm: number
  orderId: string
  orderCategory: string | null
  createdAt: Date | null
  buyerName: string | null
  productName: string | null
}

/** معرفات التجار الذين استعرضوا الطلب (من smartSearchState أو الحقل الجذري الدائم) */
function readViewedSellerIds(data: Record<string, unknown> | undefined): string[] {
  if (!data) return []
  const smart = data.smartSearchState as Record<string, unknown> | undefined
  const fromSmart = smart?.viewedSellerIds
  const fromRoot = data.viewedSellerIds
  const raw = Array.isArray(fromSmart) ? fromSmart : Array.isArray(fromRoot) ? fromRoot : []
  return [...new Set(raw.map((x) => String(x).trim()).filter(Boolean))]
}

function viewedStorageKey(orderId: string) {
  return `karsal_viewed_sellers_${orderId}`
}

function loadPersistedViewedIds(orderId: string): string[] {
  try {
    const raw = sessionStorage.getItem(viewedStorageKey(orderId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return [...new Set(parsed.map((x) => String(x).trim()).filter(Boolean))]
  } catch {
    return []
  }
}

function savePersistedViewedIds(orderId: string, ids: string[]) {
  try {
    sessionStorage.setItem(viewedStorageKey(orderId), JSON.stringify(ids))
  } catch {
    /* تجاهل — وضع خاص أو تخزين معطّل */
  }
}

/** تنسيق التوقيت للعرض في الـ Popup */
function formatDateTime(d: Date | null): string {
  if (!d) return '—'
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

/** حساب حدود مربع يحيط بدائرة (بدون استخدام L.circle.getBounds لأنه يتطلب خريطة) */
function circleBounds(centerLat: number, centerLng: number, radiusKm: number): L.LatLngBounds {
  const latDeg = radiusKm / 111
  const lngDeg = radiusKm / (111 * Math.cos((centerLat * Math.PI) / 180))
  return L.latLngBounds(
    [centerLat - latDeg, centerLng - lngDeg],
    [centerLat + latDeg, centerLng + lngDeg]
  )
}

function MapFitBounds({
  locations,
  simulation,
  searchMode,
}: {
  locations: MerchantLocation[]
  simulation: RequestSimulation | null
  searchMode: string
}) {
  const map = useMap()
  useEffect(() => {
    if (simulation) {
      if (searchMode === 'smart') {
        const points: [number, number][] = [[simulation.center[0], simulation.center[1]]]
        for (const loc of locations) {
          points.push([loc.latitude, loc.longitude])
        }
        if (points.length === 1) {
          map.setView(points[0], 11)
          return
        }
        map.fitBounds(L.latLngBounds(points), { padding: [50, 50], maxZoom: 14 })
        return
      }
      const [lat, lng] = simulation.center
      const bounds = circleBounds(lat, lng, simulation.radiusKm)
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 })
      return
    }
    if (locations.length === 0) return
    if (locations.length === 1) {
      map.setView([locations[0].latitude, locations[0].longitude], 14)
      return
    }
    const bounds = L.latLngBounds(
      locations.map((l) => [l.latitude, l.longitude] as [number, number])
    )
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
  }, [locations, simulation, searchMode, map])
  return null
}

function MapFlyToMerchant({ location, active }: { location: MerchantLocation | null; active: boolean }) {
  const map = useMap()
  useEffect(() => {
    if (!active || !location) return
    map.flyTo([location.latitude, location.longitude], 16, { duration: 0.8 })
  }, [location?.id, active, map])
  return null
}

/** الزووم بالسكرول يعمل فقط مع الضغط على Ctrl */
function MapScrollZoomControl() {
  const map = useMap()
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return
      e.preventDefault()
      const delta = e.deltaY > 0 ? -1 : 1
      map.setZoom(map.getZoom() + delta)
    }
    const container = map.getContainer()
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [map])
  return null
}

/** مجموعة معرفات التجار الحاليين + أرقام هواتفهم من users */
function useActiveSellers(): { activeSellerIds: Set<string>; sellerPhones: Record<string, string>; sellerMeta: Record<string, { isActive: boolean; lastSeenAt: Date | null }> } {
  const [activeSellerIds, setActiveSellerIds] = useState<Set<string>>(new Set())
  const [sellerPhones, setSellerPhones] = useState<Record<string, string>>({})
  const [sellerMeta, setSellerMeta] = useState<Record<string, { isActive: boolean; lastSeenAt: Date | null }>>({})
  useEffect(() => {
    const q = query(collection(db, 'users'), where('type', '==', 'seller'))
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setActiveSellerIds(new Set(snapshot.docs.map((d) => d.id)))
        const phones: Record<string, string> = {}
        const meta: Record<string, { isActive: boolean; lastSeenAt: Date | null }> = {}
        snapshot.docs.forEach((d) => {
          const data = d.data() as any
          const phone = (d.data().phone ?? '').toString().trim()
          if (phone) phones[d.id] = normalizePhoneDigits(phone)
          const rawLastSeen = data?.lastSeenAt
          const lastSeenAt =
            rawLastSeen?.toDate?.() ??
            (rawLastSeen ? new Date(rawLastSeen as string | number) : null)
          meta[d.id] = {
            isActive: data?.isActive !== false,
            lastSeenAt,
          }
        })
        setSellerPhones(phones)
        setSellerMeta(meta)
      },
      (err) => {
        console.error('MerchantsMap activeSellers error:', err)
        setActiveSellerIds(new Set())
        setSellerPhones({})
        setSellerMeta({})
      }
    )
    return () => unsub()
  }, [])
  return { activeSellerIds, sellerPhones, sellerMeta }
}

export default function MerchantsMapPage() {
  const uiLang: 'ar' | 'en' = (localStorage.getItem('i18nextLng') || '').startsWith('en') ? 'en' : 'ar'
  const [searchParams] = useSearchParams()
  const { activeSellerIds, sellerPhones, sellerMeta } = useActiveSellers()
  const [rawLocations, setRawLocations] = useState<MerchantLocation[]>([])
  const [catalog, setCatalog] = useState<CategoryCatalogItem[]>([])
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<Set<string>>(new Set())
  const [categoryFilterInitialized, setCategoryFilterInitialized] = useState(false)
  const [categorySearch, setCategorySearch] = useState('')
  const [filterOpen, setFilterOpen] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // محاكاة طلب مشتري
  const [orderIdInput, setOrderIdInput] = useState('')
  const [requestSimulation, setRequestSimulation] = useState<RequestSimulation | null>(null)
  const [sellerIdsWithOffer, setSellerIdsWithOffer] = useState<Set<string>>(new Set()) // تجار أرسلوا عرضاً لهذا الطلب
  const [sellerIdAccepted, setSellerIdAccepted] = useState<string | null>(null) // التاجر المقبول عرضه
  const [sellerIdToOfferSentAt, setSellerIdToOfferSentAt] = useState<Record<string, Date>>({}) // توقيت إرسال العرض لكل تاجر
  const [acceptedOfferAt, setAcceptedOfferAt] = useState<Date | null>(null) // توقيت قبول العرض
  const [notifiedSellerIds, setNotifiedSellerIds] = useState<string[]>([]) // البحث الذكي: من وصلهم الطلب
  const [sellerIdToRequestReceivedAt, setSellerIdToRequestReceivedAt] = useState<Record<string, Date>>({}) // وقت وصول الطلب لكل تاجر
  const [offerSummaries, setOfferSummaries] = useState<{ sellerId: string; storeName: string; price: number }[]>([])
  const [smartSearchInitialCount, setSmartSearchInitialCount] = useState<number>(5)
  const [smartSearchExpansionCount, setSmartSearchExpansionCount] = useState<number>(5)
  const [requestSearchMode, setRequestSearchMode] = useState<string>('radius') // radius | smart
  const [notAvailableSellerIds, setNotAvailableSellerIds] = useState<string[]>([]) // تجار ردوا "غير متوفر"
  const [viewedSellerIds, setViewedSellerIds] = useState<string[]>([]) // تجار استعرضوا الطلب
  /** تراكم المعرفات عبر التحديثات — لا يُعاد تصفيره عند وصول عروض جديدة */
  const viewedSellerIdsAccumRef = useRef<Set<string>>(new Set())
  const applyViewedSellerIds = useCallback(
    (ids: string[], reset = false, orderId?: string | null) => {
      if (reset) viewedSellerIdsAccumRef.current = new Set()
      for (const id of ids) {
        const t = id.trim()
        if (t) viewedSellerIdsAccumRef.current.add(t)
      }
      const list = [...viewedSellerIdsAccumRef.current]
      setViewedSellerIds(list)
      const oid = orderId ?? null
      if (oid) savePersistedViewedIds(oid, list)
    },
    []
  )
  const [simulateLoading, setSimulateLoading] = useState(false)
  const [simulateError, setSimulateError] = useState<string | null>(null)
  const [searchMerchantInput, setSearchMerchantInput] = useState('')
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null)

  // جلب كتالوج الفئات من Firestore (يدعم الأسماء القديمة عبر previousNames)
  useEffect(() => {
    const load = async () => {
      try {
        const list = await loadCategoryCatalog()
        setCatalog(list)
      } catch (err) {
        console.error('MerchantsMap categories load error:', err)
        setCatalog([])
      }
    }
    load()
  }, [])

  useEffect(() => {
    const q = query(
      collection(db, 'store_activation_requests'),
      where('status', '==', 'approved')
    )
    const unsub = onSnapshot(
      q,
      async (snapshot) => {
        const list: MerchantLocation[] = []
        for (const d of snapshot.docs) {
          const data = d.data()
          const lat = data.latitude ?? data.lat
          const lng = data.longitude ?? data.lng
          if (typeof lat === 'number' && typeof lng === 'number') {
            const cats = Array.isArray(data.requestedCategories)
              ? data.requestedCategories
              : (data.categories ? (Array.isArray(data.categories) ? data.categories : [data.categories]) : [])
            const rawCats = (cats as unknown[])
              .map((c) => (c && String(c).trim()) || '')
              .filter(Boolean) as string[]
            const phoneFromRequest = normalizePhoneDigits(
              (data.ownerPhone ?? data.phone ?? data.sellerPhone ?? '').toString()
            )
            const localizedCats = await Promise.all(
              rawCats.map((c) => localizeCategory(c, uiLang))
            )
            list.push({
              id: d.id,
              sellerId: data.sellerId || '',
              sellerName: data.sellerName || data.ownerName || 'تاجر',
              storeName: data.storeName || 'متجر',
              latitude: lat,
              longitude: lng,
              status: data.status || 'approved',
              categories: localizedCats,
              rawCategories: rawCats,
              phone: phoneFromRequest,
              isActive: true,
              lastSeenAt: null,
            })
          }
        }
        setRawLocations(list)
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('MerchantsMap snapshot error:', err)
        setError('فشل تحميل مواقع التجار')
        setLoading(false)
      }
    )
    return () => unsub()
  }, [])

  // عرض التجار الحاليين فقط مع دمج رقم الهاتف من users إن لم يكن في الطلب
  const locations = useMemo(
    () =>
      rawLocations
        .filter((loc) => loc.sellerId && activeSellerIds.has(loc.sellerId))
        .map((loc) => ({
          ...loc,
          phone: loc.phone || sellerPhones[loc.sellerId] || '',
          isActive: sellerMeta[loc.sellerId]?.isActive !== false,
          lastSeenAt: sellerMeta[loc.sellerId]?.lastSeenAt ?? null,
        })),
    [rawLocations, activeSellerIds, sellerPhones, sellerMeta]
  )

  /** مفاتيح الفئات لكل تاجر (معرّف مستقر لكل فئة) */
  const merchantCategoryKeys = useMemo(() => {
    const out = new Map<string, string[]>()
    for (const loc of locations) {
      out.set(loc.id, toCategoryKeys(loc.rawCategories, catalog))
    }
    return out
  }, [locations, catalog])

  /** عدد التجار الذين لا توجد لديهم أي فئة (مفتاح خاص في الفلتر) */
  const noCategoryCount = useMemo(
    () => locations.filter((loc) => (merchantCategoryKeys.get(loc.id) ?? []).length === 0).length,
    [locations, merchantCategoryKeys]
  )

  /** خيارات الفلتر: فئات الكتالوج + أي أسماء قديمة موجودة في بيانات التجار */
  const categoryOptions = useMemo<CategoryFilterOption[]>(() => {
    const labelByKey = new Map<string, string>()
    const counts = new Map<string, number>()

    for (const keys of merchantCategoryKeys.values()) {
      for (const k of keys) counts.set(k, (counts.get(k) ?? 0) + 1)
    }

    for (const cat of catalog) {
      labelByKey.set(cat.id, uiLang === 'en' ? cat.nameEn || cat.name : cat.name || cat.nameEn)
    }

    // أسماء قديمة لم تعد موجودة في الإعدادات — نبقيها قابلة للاختيار حتى
    // لا يختفي أي تاجر من الفلتر بسبب اسم قديم.
    for (const loc of locations) {
      for (const raw of loc.rawCategories) {
        const key = toCategoryKeys([raw], catalog)[0]
        if (!key || !key.startsWith(LEGACY_CATEGORY_PREFIX)) continue
        if (!labelByKey.has(key)) labelByKey.set(key, raw)
      }
    }

    const options: CategoryFilterOption[] = []
    for (const [key, label] of labelByKey) {
      options.push({
        key,
        label: label || key,
        isLegacy: key.startsWith(LEGACY_CATEGORY_PREFIX),
        count: counts.get(key) ?? 0,
      })
    }
    const byLabel = (a: CategoryFilterOption, b: CategoryFilterOption) =>
      a.label.localeCompare(b.label, 'ar')
    return [
      ...options.filter((o) => !o.isLegacy).sort(byLabel),
      ...options.filter((o) => o.isLegacy).sort(byLabel),
    ]
  }, [catalog, merchantCategoryKeys, locations, uiLang])

  // عند أول تحميل: تحديد كل الفئات مرة واحدة فقط
  useEffect(() => {
    if (categoryFilterInitialized) return
    if (categoryOptions.length === 0) return
    const next = new Set(categoryOptions.map((o) => o.key))
    if (noCategoryCount > 0) next.add(NO_CATEGORY_KEY)
    setSelectedCategoryKeys(next)
    setCategoryFilterInitialized(true)
  }, [categoryOptions, categoryFilterInitialized, noCategoryCount])

  // خيارات الفلتر بعد تطبيق البحث النصي داخل الفلتر
  const visibleCategoryOptions = useMemo(() => {
    const q = categorySearch.trim()
    if (!q) return categoryOptions
    return categoryOptions.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
  }, [categoryOptions, categorySearch])

  const passesCategoryFilter = useCallback(
    (loc: MerchantLocation) => {
      const keys = merchantCategoryKeys.get(loc.id) ?? []
      if (keys.length === 0) return selectedCategoryKeys.has(NO_CATEGORY_KEY)
      return keys.some((k) => selectedCategoryKeys.has(k))
    },
    [merchantCategoryKeys, selectedCategoryKeys]
  )

  const selectedCount = useMemo(
    () => locations.filter((loc) => passesCategoryFilter(loc)).length,
    [locations, passesCategoryFilter]
  )

  // الفلتر يعمل في الحالتين: بدون محاكاة طلب، وأثناءها
  const filteredLocations = useMemo(
    () => locations.filter((loc) => passesCategoryFilter(loc)),
    [locations, passesCategoryFilter]
  )

  const toggleCategory = (key: string) => {
    setSelectedCategoryKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectAllCategories = () => {
    const next = new Set(categoryOptions.map((o) => o.key))
    if (noCategoryCount > 0) next.add(NO_CATEGORY_KEY)
    setSelectedCategoryKeys(next)
  }
  const clearAllCategories = () => setSelectedCategoryKeys(new Set())

  // محاكاة الطلب: جلب الطلب ورسم النطاق وعرض التجار داخل النطاق
  // يدعم الرقم الكامل (معرف المستند) أو الرقم القصير (أول 8 أحرف كما يظهر في لوحة الطلبات)
  const runSimulation = async (overrideOrderId?: string) => {
    const id = (overrideOrderId ?? orderIdInput).trim().replace(/\s/g, '')
    if (!id) {
      setSimulateError('أدخل رقم الطلب')
      return
    }
    setSimulateLoading(true)
    setSimulateError(null)
    try {
      let snap = await getDoc(doc(db, 'requests', id))
      if (!snap.exists() && id.length <= 10) {
        const short = id.substring(0, 8)
        const q = query(
          collection(db, 'requests'),
          orderBy('createdAt', 'desc'),
          limit(300)
        )
        const recent = await getDocs(q)
        const found = recent.docs.find((d) => d.id.startsWith(short) || d.id.substring(0, 8) === short)
        if (found) snap = found
      }
      if (!snap.exists()) {
        setSimulateError('الطلب غير موجود. استخدم الرقم الكامل للطلب (معرف المستند) أو أول 8 أرقام.')
        setRequestSimulation(null)
        setSellerIdsWithOffer(new Set())
        setSellerIdAccepted(null)
        setSellerIdToOfferSentAt({})
        setAcceptedOfferAt(null)
        setNotifiedSellerIds([])
        setNotAvailableSellerIds([])
        applyViewedSellerIds([], true)
        setOfferSummaries([])
        return
      }
      const data = snap.data()
      const loc = data?.location
      const lat = loc?.latitude ?? loc?.lat
      const lng = loc?.longitude ?? loc?.lng
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        setSimulateError('الطلب لا يحتوي على موقع بحث')
        setRequestSimulation(null)
        setSellerIdsWithOffer(new Set())
        setSellerIdAccepted(null)
        setSellerIdToOfferSentAt({})
        setAcceptedOfferAt(null)
        setNotifiedSellerIds([])
        setSellerIdToRequestReceivedAt({})
        setNotAvailableSellerIds([])
        setSmartSearchInitialCount(5)
        setSmartSearchExpansionCount(5)
        applyViewedSellerIds([], true)
        setOfferSummaries([])
        return
      }
      const radiusKm = typeof data?.rangeKm === 'number' && data.rangeKm > 0 ? data.rangeKm : 10
      const orderCategoryRaw = (data?.productCategory ?? data?.category ?? '').toString().trim() || null
      const orderCategory = orderCategoryRaw ? await localizeCategory(orderCategoryRaw, uiLang) : null
      const reqCreatedAt = data?.createdAt?.toDate?.() ?? (data?.createdAt ? new Date(data.createdAt as string | number) : null)

      let buyerName: string | null = null
      const buyerId = (data?.buyerId ?? '').toString()
      if (buyerId) {
        try {
          const buyerSnap = await getDoc(doc(db, 'users', buyerId))
          if (buyerSnap.exists()) {
            const bdata = buyerSnap.data()
            buyerName = (bdata?.name as string) || null
          }
        } catch (buyerErr) {
          console.warn('MerchantsMap buyer name load error:', buyerErr)
        }
      }
      const productName =
        (data?.productName ?? data?.product_name ?? data?.name ?? '').toString().trim() || null

      setRequestSimulation({
        center: [lat, lng],
        radiusKm,
        orderId: snap.id,
        orderCategory,
        createdAt: reqCreatedAt,
        buyerName,
        productName,
      })

      const searchMode = (data?.searchMode ?? 'radius') as string
      setRequestSearchMode(searchMode)
      const smartState = data?.smartSearchState
      const notified = Array.isArray(smartState?.notifiedSellerIds) ? smartState.notifiedSellerIds as string[] : []
      setNotifiedSellerIds(notified)
      const notAvailable = Array.isArray(data?.notAvailableSellerIds) ? (data.notAvailableSellerIds as string[]) : []
      setNotAvailableSellerIds(notAvailable)
      applyViewedSellerIds(loadPersistedViewedIds(snap.id), true, snap.id)
      applyViewedSellerIds(readViewedSellerIds(data as Record<string, unknown>), false, snap.id)
      const rawNotifiedAt = smartState?.notifiedAt
      const receivedAt: Record<string, Date> = {}
      if (rawNotifiedAt && typeof rawNotifiedAt === 'object') {
        for (const [sid, t] of Object.entries(rawNotifiedAt)) {
          const d = (t as { toDate?: () => Date })?.toDate?.() ?? (t ? new Date(t as string | number) : null)
          if (d) receivedAt[sid] = d
        }
      }
      setSellerIdToRequestReceivedAt(receivedAt)
      const cfg = data?.smartSearchConfig
      setSmartSearchInitialCount(typeof cfg?.initial_merchants_count === 'number' ? cfg.initial_merchants_count : 5)
      setSmartSearchExpansionCount(typeof cfg?.wave_expansion_count === 'number' ? cfg.wave_expansion_count : 5)

      // جلب العروض: من أرسل عرضاً ومن قُبل عرضه مع تواريخ الإرسال والقبول
      try {
        const offersSnap = await getDocs(
          query(collection(db, 'offers'), where('requestId', '==', snap.id))
        )
        const ids = new Set<string>()
        const sentAt: Record<string, Date> = {}
        let acceptedId: string | null = null
        let acceptedAt: Date | null = null
         const summaries: { sellerId: string; storeName: string; price: number }[] = []
        offersSnap.docs.forEach((d) => {
          const ddata = d.data()
          const sid = ddata.sellerId
          const created = ddata.createdAt?.toDate?.() ?? (ddata.createdAt ? new Date(ddata.createdAt as string | number) : null)
          const updated = ddata.updatedAt?.toDate?.() ?? (ddata.updatedAt ? new Date(ddata.updatedAt as string | number) : null)
          if (sid) {
            ids.add(sid)
            if (created) sentAt[sid] = created
            const rawPrice = ddata.price
            let price: number
            if (typeof rawPrice === 'number') price = rawPrice
            else if (rawPrice != null) price = Number(rawPrice)
            else price = 0
            if (!Number.isFinite(price)) price = 0
            summaries.push({
              sellerId: sid,
              storeName: '',
              price,
            })
          }
          if (ddata.status === 'accepted') {
            acceptedId = sid
            acceptedAt = updated || created || null
          }
        })
        // ربط أسماء المتاجر بالعروض
        const sellerIdToStoreName: Record<string, string> = {}
        locations.forEach((loc) => {
          if (loc.sellerId) {
            sellerIdToStoreName[loc.sellerId] = loc.storeName || loc.sellerName || 'متجر'
          }
        })
        const summariesWithNames = summaries.map((s) => ({
          ...s,
          storeName:
            sellerIdToStoreName[s.sellerId] || `تاجر (${s.sellerId.substring(0, 6)})`,
        }))
        setSellerIdsWithOffer(ids)
        setSellerIdToOfferSentAt(sentAt)
        setSellerIdAccepted(acceptedId)
        setAcceptedOfferAt(acceptedAt)
        setOfferSummaries(summariesWithNames)
      } catch {
        setSellerIdsWithOffer(new Set())
        setSellerIdToOfferSentAt({})
        setSellerIdAccepted(null)
        setAcceptedOfferAt(null)
        setOfferSummaries([])
      }
    } catch (e) {
      console.error(e)
      setSimulateError('فشل تحميل الطلب')
      setRequestSimulation(null)
      setSellerIdsWithOffer(new Set())
      setSellerIdAccepted(null)
      setSellerIdToOfferSentAt({})
      setAcceptedOfferAt(null)
      setNotifiedSellerIds([])
      setSellerIdToRequestReceivedAt({})
      setNotAvailableSellerIds([])
      setSmartSearchInitialCount(5)
      setSmartSearchExpansionCount(5)
      applyViewedSellerIds([], true)
      setOfferSummaries([])
    } finally {
      setSimulateLoading(false)
    }
  }

  // دعم فتح محاكاة الطلب مباشرة من رابط مثل: /merchants-map?orderId=xxxx
  useEffect(() => {
    const orderIdFromUrl = (searchParams.get('orderId') || '').trim()
    if (!orderIdFromUrl) return
    setOrderIdInput(orderIdFromUrl)
    void runSimulation(orderIdFromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const clearSimulation = () => {
    setRequestSimulation(null)
    setSellerIdsWithOffer(new Set())
    setSellerIdAccepted(null)
    setSellerIdToOfferSentAt({})
    setAcceptedOfferAt(null)
    setNotifiedSellerIds([])
    setSellerIdToRequestReceivedAt({})
    setNotAvailableSellerIds([])
    setSmartSearchInitialCount(5)
    setSmartSearchExpansionCount(5)
    setSimulateError(null)
    setOrderIdInput('')
    applyViewedSellerIds([], true)
    setOfferSummaries([])
  }

  // متابعة حالة العروض في الوقت الحقيقي لنفس الطلب لمحاكاة الخريطة (تحديث لون الدائرة عند قبول عرض)
  useEffect(() => {
    if (!requestSimulation) {
      return
    }

    const offersQuery = query(
      collection(db, 'offers'),
      where('requestId', '==', requestSimulation.orderId)
    )

    const unsub = onSnapshot(
      offersQuery,
      (offersSnap) => {
        const ids = new Set<string>()
        const sentAt: Record<string, Date> = {}
        let acceptedId: string | null = null
        let acceptedAt: Date | null = null
        const summaries: { sellerId: string; storeName: string; price: number }[] = []

        offersSnap.docs.forEach((d) => {
          const ddata = d.data() as any
          const sid = ddata.sellerId as string | undefined
          const created =
            ddata.createdAt?.toDate?.() ??
            (ddata.createdAt ? new Date(ddata.createdAt as string | number) : null)
          const updated =
            ddata.updatedAt?.toDate?.() ??
            (ddata.updatedAt ? new Date(ddata.updatedAt as string | number) : null)

          if (sid) {
            ids.add(sid)
            if (created) sentAt[sid] = created

            const rawPrice = ddata.price
            let price: number
            if (typeof rawPrice === 'number') price = rawPrice
            else if (rawPrice != null) price = Number(rawPrice)
            else price = 0
            if (!Number.isFinite(price)) price = 0

            summaries.push({
              sellerId: sid,
              storeName: '',
              price,
            })
          }

          if (ddata.status === 'accepted') {
            acceptedId = sid ?? null
            acceptedAt = updated || created || null
          }
        })

        // ربط أسماء المتاجر بالعروض (من قائمة المواقع الحالية)
        const sellerIdToStoreName: Record<string, string> = {}
        locations.forEach((loc) => {
          if (loc.sellerId) {
            sellerIdToStoreName[loc.sellerId] =
              loc.storeName || loc.sellerName || 'متجر'
          }
        })

        const summariesWithNames = summaries.map((s) => ({
          ...s,
          storeName:
            sellerIdToStoreName[s.sellerId] ||
            `تاجر (${s.sellerId.substring(0, 6)})`,
        }))

        setSellerIdsWithOffer(ids)
        setSellerIdToOfferSentAt(sentAt)
        setSellerIdAccepted(acceptedId)
        setAcceptedOfferAt(acceptedAt)
        setOfferSummaries(summariesWithNames)
      },
      (err) => {
        console.error('MerchantsMap offers listener error:', err)
        setSellerIdsWithOffer(new Set())
        setSellerIdToOfferSentAt({})
        setSellerIdAccepted(null)
        setAcceptedOfferAt(null)
        setOfferSummaries([])
      }
    )

    return () => unsub()
  }, [requestSimulation, locations])

  // متابعة مستند الطلب لتحديث notAvailableSellerIds و notifiedSellerIds عند رد التاجر "غير متوفر"
  useEffect(() => {
    if (!requestSimulation) return
    const unsub = onSnapshot(
      doc(db, 'requests', requestSimulation.orderId),
      (snap) => {
        if (!snap.exists()) return
        const data = snap.data()
        const smartState = data?.smartSearchState
        const notified = Array.isArray(smartState?.notifiedSellerIds) ? (smartState.notifiedSellerIds as string[]) : []
        setNotifiedSellerIds(notified)
        const notAvailable = Array.isArray(data?.notAvailableSellerIds) ? (data.notAvailableSellerIds as string[]) : []
        setNotAvailableSellerIds(notAvailable)
        applyViewedSellerIds(
          readViewedSellerIds(data as Record<string, unknown>),
          false,
          requestSimulation.orderId
        )
      },
      () => {}
    )
    return () => unsub()
  }, [requestSimulation?.orderId, applyViewedSellerIds])

  useEffect(() => {
    if (!requestSimulation || offerSummaries.length === 0) return
    applyViewedSellerIds(
      offerSummaries.map((o) => o.sellerId).filter(Boolean),
      false,
      requestSimulation.orderId
    )
  }, [requestSimulation?.orderId, offerSummaries, applyViewedSellerIds])

  const viewedMerchantEntries = useMemo(() => {
    return viewedSellerIds.map((sid) => {
      const loc = locations.find((l) => l.sellerId === sid)
      if (loc) {
        return { sellerId: sid, name: (loc.storeName || loc.sellerName).trim() || sid }
      }
      const offer = offerSummaries.find((o) => o.sellerId === sid)
      if (offer?.storeName) {
        return { sellerId: sid, name: offer.storeName.trim() }
      }
      return { sellerId: sid, name: `تاجر (${sid.slice(0, 8)}…)` }
    })
  }, [viewedSellerIds, locations, offerSummaries])

  const viewedMerchantsListUi = (
    <>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        متاجر استعرضوا طلبك ({viewedMerchantEntries.length}):
      </Typography>
      {viewedMerchantEntries.length > 0 ? (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {viewedMerchantEntries.map((e) => (
            <Typography key={e.sellerId} component="li" variant="body2" sx={{ py: 0.15 }}>
              {e.name}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          —
        </Typography>
      )}
    </>
  )

  // عند المحاكاة: بحث ذكي = من وصلهم الطلب فقط (notifiedSellerIds)، بحث نطاق = داخل النطاق + الفئة.
  // فلتر الفئات يُطبَّق دائماً (قبل المحاكاة وأثناءها) ليبقى فعّالاً في الحالتين.
  const displayLocations = useMemo(() => {
    if (requestSimulation) {
      if (requestSearchMode === 'smart') {
        if (notifiedSellerIds.length === 0) return []
        const notifiedSet = new Set(notifiedSellerIds)
        return filteredLocations.filter((loc) => notifiedSet.has(loc.sellerId))
      }
      const [centerLat, centerLng] = requestSimulation.center
      const r = requestSimulation.radiusKm
      const orderCat = requestSimulation.orderCategory
      const inRangeAndCategory = (loc: MerchantLocation) => {
        if (distanceKm(centerLat, centerLng, loc.latitude, loc.longitude) > r) return false
        if (!orderCat) return true
        return loc.categories.some(
          (c) => c.trim() === orderCat || c.includes(orderCat) || orderCat.includes(c.trim())
        )
      }
      return filteredLocations.filter(inRangeAndCategory)
    }
    return filteredLocations
  }, [requestSimulation, filteredLocations, notifiedSellerIds, requestSearchMode])

  // من وصلهم الطلب: بحث ذكي = notifiedSellerIds، بحث نطاق = داخل النطاق+الفئة
  const sellerIdsReceivedRequest = useMemo(() => {
    if (!requestSimulation) return new Set<string>()
    if (requestSearchMode === 'smart') return new Set(notifiedSellerIds)
    return new Set(displayLocations.map((l) => l.sellerId))
  }, [requestSimulation, requestSearchMode, notifiedSellerIds, displayLocations])

  // رقم الموجة لكل تاجر (بحث ذكي): من ترتيبه في notifiedSellerIds
  const sellerIdToWaveNumber = useMemo(() => {
    const out: Record<string, number> = {}
    const initial = smartSearchInitialCount
    const expansion = Math.max(1, smartSearchExpansionCount)
    notifiedSellerIds.forEach((id, i) => {
      out[id] = i < initial ? 1 : 1 + Math.floor((i - initial) / expansion)
    })
    return out
  }, [notifiedSellerIds, smartSearchInitialCount, smartSearchExpansionCount])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box pt={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (locations.length === 0) {
    return (
      <Box pt={2}>
        <Typography variant="h6" color="text.secondary">
          لا توجد مواقع للتجار المعتمدين لعرضها على الخريطة.
        </Typography>
      </Box>
    )
  }

  const center: [number, number] = requestSimulation
    ? requestSimulation.center
    : displayLocations.length > 0
      ? [displayLocations[0].latitude, displayLocations[0].longitude]
      : [26.8206, 30.8025]

  return (
    <Box>
      <style>{`.merchant-k-marker.leaflet-marker-icon { background: transparent !important; border: none !important; }`}</style>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        خريطة التجار
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {requestSimulation
          ? requestSearchMode === 'smart'
            ? `محاكاة الطلب ${requestSimulation.orderId}${requestSimulation.orderCategory ? ` (فئة: ${requestSimulation.orderCategory})` : ''}: ${displayLocations.length} تاجر وصلهم الطلب (بحث ذكي — موجة أولى ${smartSearchInitialCount} ثم +${smartSearchExpansionCount})`
            : `محاكاة الطلب ${requestSimulation.orderId}${requestSimulation.orderCategory ? ` (فئة: ${requestSimulation.orderCategory})` : ''}: ${displayLocations.length} تاجر داخل النطاق ومختصون بهذه الفئة`
          : `مواقع المتاجر المعتمدة (${displayLocations.length}${displayLocations.length !== locations.length ? ` من ${locations.length}` : ''})`}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          بحث عن تاجر على الخريطة
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          ابحث بالاسم أو رقم الهاتف أو أي جزء منهما
        </Typography>
        <Autocomplete
          size="small"
          options={displayLocations}
          getOptionLabel={(loc) => `${loc.storeName} — ${loc.sellerName}${loc.phone ? ` (${loc.phone})` : ''}`}
          filterOptions={(options, state) => {
            const input = (state.inputValue || '').trim()
            const digits = normalizePhoneDigits(state.inputValue)
            if (!input) return options
            return options.filter((loc) => {
              if (digits && loc.phone && loc.phone.includes(digits)) return true
              const namePart = input.toLowerCase()
              if (namePart && (loc.storeName.toLowerCase().includes(namePart) || (loc.sellerName && loc.sellerName.toLowerCase().includes(namePart)))) return true
              return false
            })
          }}
          inputValue={searchMerchantInput}
          onInputChange={(_, value) => setSearchMerchantInput(value)}
          onChange={(_, loc) => {
            if (loc) {
              setSelectedMerchantId(loc.id)
              setSearchMerchantInput(`${loc.storeName} — ${loc.sellerName}${loc.phone ? ` (${loc.phone})` : ''}`)
            } else {
              setSelectedMerchantId(null)
            }
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="الاسم أو رقم الهاتف أو جزء منهما..." />
          )}
          sx={{ maxWidth: 400 }}
        />
        {selectedMerchantId && (
          <Button size="small" sx={{ mt: 1 }} onClick={() => { setSelectedMerchantId(null); setSearchMerchantInput('') }}>
            إلغاء التمييز
          </Button>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          محاكاة طلب مشتري
        </Typography>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
          <TextField
            size="small"
            label="رقم الطلب"
            value={orderIdInput}
            onChange={(e) => setOrderIdInput(e.target.value)}
            placeholder="الرقم الكامل (مثل 1771880551265944) أو أول 8 أرقام (17718805)"
            helperText="في لوحة الطلبات يظهر رقم مختصر (8 أرقام). يمكنك إدخاله أو المعرف الكامل للطلب."
            sx={{ minWidth: 280 }}
            onKeyDown={(e) => e.key === 'Enter' && void runSimulation()}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => void runSimulation()}
            disabled={simulateLoading}
          >
            {simulateLoading ? 'جاري التحميل...' : 'محاكاة الطلب'}
          </Button>
          {requestSimulation && (
            <Button variant="outlined" size="small" color="secondary" onClick={clearSimulation}>
              مسح المحاكاة
            </Button>
          )}
        </Box>
        {simulateError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setSimulateError(null)}>
            {simulateError}
          </Alert>
        )}
        {requestSimulation && (
          <>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2">
                عدد العروض المستلمة: <strong>{offerSummaries.length}</strong>
              </Typography>
              {viewedMerchantsListUi}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {requestSearchMode === 'smart'
                ? 'البحث الذكي: تظهر فقط المتاجر التي وصلها الطلب (بدون دائرة نطاق). الحد الجغرافي الوحيد: محافظة المشتري إن كان مفعّلاً من الإعدادات. أيقونة المتجر = وصل الطلب — '
                : 'البحث بالنطاق: تظهر المتاجر داخل دائرة النطاق والفئة. أيقونة المتجر = وصل الطلب — '}
              <strong style={{ color: '#d32f2f' }}>✕</strong> غير متوفر — <strong>✓</strong> أرسل عرضاً — <strong>✓✓</strong> قُبل عرضه — دائرة المشتري تتحول للأخضر عند القبول. (انقر دائرة المشتري على الخريطة لمزيد من التفاصيل)
            </Typography>
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight="bold">
              فلتر الفئات
            </Typography>
            <Chip
              size="small"
              color={selectedCount === locations.length ? 'default' : 'primary'}
              label={`${selectedCount} من ${locations.length} تاجر`}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Button size="small" onClick={selectAllCategories} sx={{ mr: 0.5 }}>
              تحديد الكل
            </Button>
            <Button size="small" onClick={clearAllCategories}>
              إلغاء الكل
            </Button>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          حدّد فئة أو أكثر لعرض التجار المسجّلين فيها فقط. الرقم بجانب كل فئة = عدد التجار.
        </Typography>

        <Collapse in={filterOpen}>
          <TextField
            size="small"
            fullWidth
            placeholder="ابحث عن فئة..."
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            sx={{ mt: 1.5 }}
          />
          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
              gap: 0.25,
              maxHeight: 260,
              overflow: 'auto',
              pr: 0.5,
            }}
          >
            {visibleCategoryOptions.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                لا توجد فئات مطابقة للبحث.
              </Typography>
            )}
            {visibleCategoryOptions.map((opt) => {
              const selected = selectedCategoryKeys.has(opt.key)
              return (
                <Tooltip
                  key={opt.key}
                  title={opt.isLegacy ? 'اسم فئة قديم لم يعد موجوداً في الإعدادات' : opt.label}
                  placement="top"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selected}
                        onChange={() => toggleCategory(opt.key)}
                        size="small"
                        disabled={opt.count === 0 && !selected}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ opacity: opt.count === 0 ? 0.55 : 1 }}>
                          {opt.label}
                        </Typography>
                        {opt.isLegacy && (
                          <Typography variant="caption" color="warning.main" noWrap>
                            (قديم)
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" noWrap>
                          ({opt.count})
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </Tooltip>
              )
            })}
            {noCategoryCount > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedCategoryKeys.has(NO_CATEGORY_KEY)}
                    onChange={() => toggleCategory(NO_CATEGORY_KEY)}
                    size="small"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      بدون فئة محددة
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({noCategoryCount})
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            )}
          </Box>
        </Collapse>

        <Button size="small" onClick={() => setFilterOpen((o) => !o)} sx={{ mt: 1 }}>
          {filterOpen ? 'إخفاء الفلتر' : 'إظهار الفلتر'}
        </Button>
      </Paper>

      <Box sx={{ height: 600, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <MapContainer
          center={center}
          zoom={8}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <MapScrollZoomControl />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {requestSimulation && (
            <>
              {requestSearchMode !== 'smart' && (
                <Circle
                  center={requestSimulation.center}
                  radius={requestSimulation.radiusKm * 1000}
                  pathOptions={{
                    color: sellerIdAccepted ? '#2e7d32' : '#1976d2',
                    fillColor: sellerIdAccepted ? '#2e7d32' : '#1976d2',
                    fillOpacity: 0.15,
                    weight: 2,
                  }}
                />
              )}
              <Marker
                position={requestSimulation.center}
                icon={L.divIcon({
                  className: 'karsal-request-center-marker',
                  // لون دائرة المركز: أزرق في البداية، أخضر عند قبول عرض (نفس منطق الدائرة الخارجية)
                  html: `<div style="width:36px;height:36px;border-radius:50%;background:${sellerIdAccepted ? '#2e7d32' : '#1976d2'};color:white;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;box-shadow:0 0 6px rgba(0,0,0,0.4);border:2px solid white;">${offerSummaries.length}</div>`,
                  iconSize: [36, 36],
                  iconAnchor: [18, 18],
                  popupAnchor: [0, -18],
                })}
              >
                <Popup>
                  {requestSimulation.productName && (
                    <>
                      <Typography variant="subtitle2" fontWeight="bold">
                        المنتج: {requestSimulation.productName}
                      </Typography>
                    </>
                  )}
                  {requestSimulation.buyerName && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      المشتري: {requestSimulation.buyerName}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    عدد العروض المستلمة: <strong>{offerSummaries.length}</strong>
                  </Typography>
                  {viewedMerchantsListUi}
                  {offerSummaries.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {offerSummaries.map((o) => (
                        <Typography
                          key={`${o.sellerId}-${o.price}-${o.storeName}`}
                          variant="caption"
                          display="block"
                        >
                          {o.storeName}: {o.price.toLocaleString('ar-EG')} جنيه
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Popup>
              </Marker>
            </>
          )}
          <MapFitBounds locations={displayLocations} simulation={requestSimulation} searchMode={requestSearchMode} />
          <MapFlyToMerchant
            location={displayLocations.find((l) => l.id === selectedMerchantId) ?? null}
            active={!!selectedMerchantId}
          />
          {displayLocations.map((loc) => {
            const notAvailable = requestSimulation && notAvailableSellerIds.includes(loc.sellerId)
            const received = requestSimulation ? sellerIdsReceivedRequest.has(loc.sellerId) : false
            const sentOffer = requestSimulation ? sellerIdsWithOffer.has(loc.sellerId) : false
            const accepted = requestSimulation ? loc.sellerId === sellerIdAccepted : false
            const isHighlighted = loc.id === selectedMerchantId

            let status: MerchantRequestStatus = 'none'
            if (accepted) status = 'offerAccepted'
            else if (notAvailable) status = 'notAvailable'
            else if (sentOffer) status = 'offerSent'

            return (
              <Marker
                key={loc.id}
                position={[loc.latitude, loc.longitude]}
                icon={createMerchantKIcon({ highlighted: isHighlighted, status, isActive: loc.isActive })}
              >
                <Popup>
                  <strong>{loc.storeName}</strong>
                  <br />
                  {loc.sellerName}
                  <br />
                  <Typography component="span" variant="caption" sx={{ color: loc.isActive ? 'success.main' : 'text.secondary', fontWeight: 700 }}>
                    {loc.isActive ? 'نشط' : 'غير نشط'}
                  </Typography>
                  <br />
                  <Typography component="span" variant="caption" color="text.secondary">
                    آخر ظهور: {formatDateTime(loc.lastSeenAt)}
                  </Typography>
                  {notAvailable && (
                    <>
                      <br />
                      <Typography component="span" variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                        ✕ رد: غير متوفر
                      </Typography>
                    </>
                  )}
                  {received && !notAvailable && (
                    <>
                      <br />
                      <Typography component="span" variant="caption" sx={{ whiteSpace: 'nowrap', display: 'inline-block' }}>
                        <Typography component="span" variant="caption" sx={{ color: 'info.main', fontWeight: 600 }}>
                          ✓ وصل الطلب
                        </Typography>
                        {requestSearchMode === 'smart' && sellerIdToWaveNumber[loc.sellerId] != null && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}موجة {sellerIdToWaveNumber[loc.sellerId]}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}{formatDateTime(requestSearchMode === 'smart' ? (sellerIdToRequestReceivedAt[loc.sellerId] ?? requestSimulation?.createdAt ?? null) : (requestSimulation?.createdAt ?? null))}
                        </Typography>
                      </Typography>
                    </>
                  )}
                  {sentOffer && (
                    <>
                      <br />
                      <Typography component="span" variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                        ✓ أرسل عرضاً
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}{formatDateTime(sellerIdToOfferSentAt[loc.sellerId] ?? null)}
                      </Typography>
                    </>
                  )}
                  {accepted && (
                    <>
                      <br />
                      <Typography component="span" variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        ✓✓ قُبل عرضه
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}{formatDateTime(acceptedOfferAt)}
                      </Typography>
                    </>
                  )}
                  {loc.categories.length > 0 && (
                    <>
                      <br />
                      <Typography component="span" variant="caption" color="text.secondary">
                        {loc.categories.join('، ')}
                      </Typography>
                    </>
                  )}
                </Popup>
              </Marker>
            )
          })}
        </MapContainer>
      </Box>
    </Box>
  )
}
